import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Download, Check } from "lucide-react";
import {
    createDataExport,
    fetchDataExportStatus,
    fetchLatestDataExport,
    clearDataExportError,
} from "../../redux/ApiSlice/DataExportSlice";

const ACTIVE_STATUSES = ["pending", "processing"];

const getStatus = (exportRecord) => (exportRecord?.status || "").toLowerCase();

const getDownloadUrl = (exportRecord) =>
    exportRecord?.download_url ||
    (exportRecord?.download_token
        ? `${process.env.REACT_APP_MAIN_URL}api/data-export/download/${exportRecord.download_token}/`
        : null);

const formatFileSize = (bytes) => {
    if (!bytes) return null;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
};

const formatDate = (isoDate) => {
    if (!isoDate) return null;
    return new Date(isoDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const formatElapsed = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const STEPS = [
    { key: "pending", label: "Queued" },
    { key: "processing", label: "Preparing" },
    { key: "ready", label: "Ready" },
];

const AccountProfileData = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const email = useSelector((state) => state.AccountProfile.User_profile?.email);
    const { currentExport, creating, loading, error } = useSelector((state) => state.DataExport);

    const pollingIntervalRef = useRef(null);
    const [nowTick, setNowTick] = useState(() => Date.now());

    const status = getStatus(currentExport);
    const downloadUrl = getDownloadUrl(currentExport);
    const isReady = status === "ready" && !!downloadUrl;
    const isActive = ACTIVE_STATUSES.includes(status) && !isReady;
    const isFailed = status === "failed" || (!!currentExport?.error_message && !isActive && !isReady);

    const currentStepIndex = isReady
        ? 2
        : status === "processing" || currentExport?.processing_started_at
            ? 1
            : 0;

    const startedAt = currentExport?.processing_started_at || currentExport?.requested_at;
    const elapsedSeconds = isActive && startedAt
        ? Math.max(0, Math.floor((nowTick - new Date(startedAt).getTime()) / 1000))
        : null;

    useEffect(() => {
        dispatch(fetchLatestDataExport());
    }, [dispatch]);

    // Poll export status every 7s while it's pending/processing
    useEffect(() => {
        const active = ACTIVE_STATUSES.includes(getStatus(currentExport)) && !getDownloadUrl(currentExport);

        if (!active) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        if (pollingIntervalRef.current) return;

        const poll = () => {
            dispatch(fetchDataExportStatus(currentExport.id));
        };

        pollingIntervalRef.current = setInterval(poll, 7000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [currentExport?.status, currentExport?.id, dispatch]);

    // Tick every second while active so the elapsed-time counter feels alive
    useEffect(() => {
        if (!isActive) return;
        const timer = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [isActive]);

    const handleRequest = async () => {
        if (creating || isActive) return;
        dispatch(clearDataExportError());
        try {
            await dispatch(createDataExport()).unwrap();
        } catch {
            // surfaced via `error` below (e.g. 409 already pending, 429 rate limited)
        }
    };

    const handleDownload = () => {
        if (!downloadUrl) return;
        window.open(downloadUrl, "_blank");
    };

    const errorMessage =
        error?.detail ||
        (isFailed && currentExport?.error_message) ||
        (error && "Something went wrong. Please try again.");

    return (
        <div className="h-full flex flex-col bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-50 flex-shrink-0">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/account")}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center font-bold text-[16px] text-black">Profile Data</p>
                </div>
            </div>

            <div className="flex-1 px-3 flex flex-col justify-between pb-6 pt-5">
                <div className="flex flex-col gap-2">
                    <p className="text-[14px] text-black dark:text-white font-bold">Request & Download Profile Data</p>
                    <p className="text-[12px] text-black dark:text-white">
                        Download a copy of your profile data, as required by GDPR. This includes your
                        account preferences, profile details, and any photos or videos you've uploaded.
                        Preparing your data may take some time. We'll email you when your archive is ready.
                    </p>
                    <p className="text-[12px] text-black dark:text-white">
                        Please note: If you delete your account before the export is complete, we won't be able
                        to fulfill your request. Once your data is prepared, you'll receive a download link at
                        your verified email ({email ?? "—"}).
                    </p>
                </div>

                <div className="flex flex-col gap-2 mt-6">
                    {loading && !currentExport && (
                        <div className="flex items-center gap-2 justify-center text-black dark:text-white text-[13px]">
                            <Loader2 size={15} className="animate-spin" />
                            Checking export status…
                        </div>
                    )}

                    {(isActive || isReady) && (
                        <div className="flex items-center justify-center gap-1 mb-1">
                            {STEPS.map((step, i) => (
                                <div key={step.key} className="flex items-center flex-1 last:flex-none">
                                    <div className="flex flex-col items-center gap-1">
                                        <div
                                            className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                                                ${i < currentStepIndex ? "bg-[#BDE6FF] text-black" : ""}
                                                ${i === currentStepIndex ? "bg-[#BDE6FF] text-black" : ""}
                                                ${i > currentStepIndex ? "bg-gray-200 dark:bg-gray-700 text-gray-400" : ""}`}
                                        >
                                            {i < currentStepIndex ? (
                                                <Check size={13} />
                                            ) : i === currentStepIndex && isActive ? (
                                                <Loader2 size={13} className="animate-spin" />
                                            ) : i === currentStepIndex ? (
                                                <Check size={13} />
                                            ) : (
                                                i + 1
                                            )}
                                        </div>
                                        <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {step.label}
                                        </span>
                                    </div>
                                    {i < STEPS.length - 1 && (
                                        <div
                                            className={`h-[2px] flex-1 mx-1 -mt-4 ${i < currentStepIndex ? "bg-[#BDE6FF]" : "bg-gray-200 dark:bg-gray-700"}`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {isActive && (
                        <div className="flex flex-col items-center gap-1 text-black dark:text-white text-[13px]">
                            <div className="flex items-center gap-2">
                                <Loader2 size={15} className="animate-spin" />
                                {status === "pending" ? "Request queued…" : "Preparing your archive…"}
                                {elapsedSeconds !== null && ` (${formatElapsed(elapsedSeconds)})`}
                            </div>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                This usually takes 30 seconds to a few minutes. We're checking automatically — no need to reload.
                            </p>
                        </div>
                    )}

                    {isReady && (
                        <div className="flex flex-col gap-1 items-center text-green-600 dark:text-green-400 text-[13px]">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 size={15} />
                                Your archive is ready to download{formatFileSize(currentExport.file_size_bytes ?? currentExport.file_size) ? ` (${formatFileSize(currentExport.file_size_bytes ?? currentExport.file_size)})` : ""}.
                            </div>
                            {formatDate(currentExport.download_expires_at) && (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                    Link valid until {formatDate(currentExport.download_expires_at)}
                                </p>
                            )}
                        </div>
                    )}

                    {isFailed && (
                        <div className="flex items-center gap-2 justify-center text-red-500 text-[13px]">
                            <XCircle size={15} />
                            Export failed. Please try again.
                        </div>
                    )}

                    {errorMessage && (
                        <p className="text-[12px] text-red-500 text-center">{errorMessage}</p>
                    )}

                    {isReady ? (
                        <button
                            onClick={handleDownload}
                            className="flex items-center justify-center gap-2 text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg"
                        >
                            <Download size={14} />
                            Download archive
                        </button>
                    ) : (
                        <button
                            onClick={handleRequest}
                            disabled={creating || isActive}
                            className="flex items-center justify-center gap-2 text-[13px] w-full bg-[#BDE6FF] text-black font-bold py-3 rounded-lg disabled:opacity-60"
                        >
                            {creating && <Loader2 size={14} className="animate-spin" />}
                            {creating
                                ? "Requesting…"
                                : isActive
                                    ? "Request in progress…"
                                    : status === "failed" || status === "expired"
                                        ? "Request new profile data"
                                        : "Request profile data"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountProfileData;
